/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button, Input, TextArea, Modal } from '@adobe-commerce/elsie/components';
import '@/quote-management/components/RenameQuoteModal/RenameQuoteModal.css';

export interface RenameQuoteModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  quoteName: string;
  renameReason: string;
  quoteNameError?: string;
  errorBanner?: VNode;
  successBanner?: VNode;
  showCloseButton?: boolean;
  onQuoteNameChange: (value: string) => void;
  onRenameReasonChange: (value: string) => void;
  onSave: () => void;
  onClose?: () => void;
}

export const RenameQuoteModal: FunctionComponent<RenameQuoteModalProps> = ({
  className,
  open,
  quoteName,
  renameReason,
  quoteNameError,
  errorBanner,
  successBanner,
  showCloseButton = true,
  onQuoteNameChange,
  onRenameReasonChange,
  onSave,
  onClose,
}) => {
  const dictionary = useText({
    title: 'NegotiableQuote.Manage.rename.title',
    quoteNameLabel: 'NegotiableQuote.Manage.rename.quoteNameLabel',
    reasonLabel: 'NegotiableQuote.Manage.rename.reasonLabel',
    renameButton: 'NegotiableQuote.Manage.rename.renameButton',
    cancelButton: 'NegotiableQuote.Manage.rename.cancelButton',
  });

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      size="medium"
      title={<>{dictionary.title}</>}
      onClose={onClose}
      clickToDismiss={true}
      escapeToDismiss={true}
      showCloseButton={showCloseButton}
      className={classes(['quote-management-rename-quote-modal', className])}
      data-testid="rename-quote-modal"
    >
      {errorBanner && (
        <div className={classes(['quote-management-rename-quote-modal__error-banner'])} data-testid="rename-quote-modal-error-banner">
          {errorBanner}
        </div>
      )}

      {successBanner && (
        <div className={classes(['quote-management-rename-quote-modal__success-banner'])} data-testid="rename-quote-modal-success-banner">
          {successBanner}
        </div>
      )}

      <div className={classes(['quote-management-rename-quote-modal__content'])}>
        <div>
          <Input
            name="quoteName"
            type="text"
            value={quoteName}
            onInput={(e: any) => onQuoteNameChange(e.target.value)}
            floatingLabel={dictionary.quoteNameLabel}
            required
            error={!!quoteNameError}
          />
          {quoteNameError && (
            <span className={classes(['quote-management-rename-quote-modal__error-text'])}>
              {quoteNameError}
            </span>
          )}
        </div>

        <TextArea
          name="quoteComment"
          value={renameReason}
          onInput={(e: any) => onRenameReasonChange(e.target.value)}
          label={dictionary.reasonLabel}
          rows={3}
        />
      </div>

      <div className={classes(['quote-management-rename-quote-modal__actions'])}>
        <Button 
          variant="secondary"
          size="medium"
          onClick={onClose}
          className={classes(['quote-management-rename-quote-modal__cancel-button'])}
        >
          {dictionary.cancelButton}
        </Button>
        <Button 
          variant="primary"
          size="medium"
          onClick={onSave}
          className={classes(['quote-management-rename-quote-modal__save-button'])}
        >
          {dictionary.renameButton}
        </Button>
      </div>
    </Modal>
  );
};
